import { type DetailedHTMLProps, type HTMLAttributes, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useScript } from "~/common/hooks/useScript";
import { userIsCustomer, userIsRegistered } from "~/domains/user/utils/roles";
import { CURRENT_LANGUAGE } from "~/utils/constants";
import { useAppConfig } from "~/app/config/useAppConfig";
import { lubricationSelectionAssistantPageQueryOptions } from "~/app/api/pageData/lubricationSelectionAssistantPageQuery";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { getInitialPriceReference } from "~/features/addToCart/redux/addToCart/getInitialPriceReference";
import { productHasInStock } from "~/domains/product/utils/productDescriptorUtils";
import { useDispatch } from "react-redux";
import { addToCartThunk } from "~/features/selectionAssistant/lubrication/AddToCartThunk";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { PurchasePlaceProvider, PurchaseTypeProvider } from "~/app/analytics/ga/recommendations/RecommendationContexts";
import type { PurchaseType } from "~/app/analytics/ga/recommendations/types";
import { usePriceReferenceSelection } from "~/app/config/useSession";

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      "lubricator-selection-assistant": DetailedHTMLProps<
        HTMLAttributes<HTMLElement> & {
          language?: string;
          "user-tier"?: "anonymous" | "plus" | "business";
        },
        HTMLElement
      >;
    }
  }
}

interface RequestAvailabilityEvent {
  details: { payload: { pimIds: string[] } };
  callback: (results: MediasCallbackResponse) => void;
}

interface RequestAvailabilityItemResponse {
  available?: boolean;
  price?: number;
  currency: string;
}

interface MediasCallbackResponse {
  items: {
    // key matches the medias PIM id for the item
    [key: string]: RequestAvailabilityItemResponse;
  };
}

interface AddToCartEvent {
  cart: { productCode: string; packCode?: string; quantity: number }[];
}

export const LubricationSelectionAssistantPage = () => {
  const currencyIso = useAppConfig()?.formatting.currencyIso ?? "";
  const pageDataResult = useQuery(lubricationSelectionAssistantPageQueryOptions()).data;
  const lsaWebComponentUrl = pageDataResult?.status === "OK" ? pageDataResult.data.lsaWebComponentUrl : "";
  useScript(lsaWebComponentUrl);
  const ref = useRef<HTMLElement>(null);
  const { selection, status } = usePriceReferenceSelection();

  const dispatch = useDispatch();

  const purchasePlace = "LSA" as const;
  const purchaseType: PurchaseType = "lsa" as const;

  const addToCartHandler = useCallback(
    (e: CustomEvent<AddToCartEvent>) => {
      if (status !== "ready") {
        return;
      }
      const { cart } = e.detail;

      let items = cart.filter(({ quantity }) => quantity > 0);
      if (items.length > 0) {
        if (userIsCustomer()) {
          items = items.map((item) => ({
            ...item,
            priceReference: getInitialPriceReference(selection),
          }));
        }
        dispatch(
          addToCartThunk(items, "", {
            page: purchasePlace,
            priceReference: getInitialPriceReference(selection),
            recommendationPlace: purchasePlace,
            recommendationType: purchaseType,
          }),
        );
      }
    },
    [purchasePlace, purchaseType, selection, status],
  );

  const requestAvailabilityHandler = useCallback(
    async (e: CustomEvent<RequestAvailabilityEvent>) => {
      if (status !== "ready") {
        return;
      }
      const {
        details: {
          payload: { pimIds },
        },
        callback,
      } = e.detail;

      const promises = pimIds.map((productCode) => {
        return ProductService.getProductDescriptor(productCode, getInitialPriceReference(selection), {
          fetch: true,
        }).promise as Promise<ProductDescriptor>;
      });
      const results = await Promise.allSettled(promises);

      if (userIsCustomer()) {
        const items = results.reduce(
          (acc, result) => {
            if (result.status === "fulfilled") {
              const product = result.value;
              const available = productHasInStock(product);
              const price = product.prices[0]?.priceScale[0]?.price?.value ?? undefined;
              const currency = currencyIso;
              acc[product.base.code] = {
                available,
                price,
                currency,
              };
            }
            return acc;
          },
          {} as Record<string, RequestAvailabilityItemResponse>,
        );
        const blankValue = {
          currency: currencyIso,
        };
        for (const pimId of pimIds) {
          if (!items[pimId]) {
            items[pimId] = blankValue;
          }
        }
        callback({ items });
      } else {
        const items = pimIds.reduce(
          (acc, productCode) => {
            const currency = currencyIso;
            acc[productCode] = {
              currency,
            };
            return acc;
          },
          {} as Record<string, RequestAvailabilityItemResponse>,
        );
        callback({ items });
      }
    },
    [currencyIso, selection, status],
  );
  useEffect(() => {
    if (ref.current) {
      // @ts-ignore
      ref.current.addEventListener("addToCart", addToCartHandler);
      // @ts-ignore
      ref.current.addEventListener("availabilityRequest", requestAvailabilityHandler);
      return () => {
        // @ts-ignore
        ref.current?.removeEventListener("addToCart", addToCartHandler);
        // @ts-ignore
        ref.current?.removeEventListener("availabilityRequest", requestAvailabilityHandler);
      };
    }
  }, [ref.current]);
  return (
    <PurchasePlaceProvider value={"LSA"}>
      <PurchaseTypeProvider value={"lsa"}>
        <div className={"scha__ignore-margins"}>
          <lubricator-selection-assistant
            ref={ref}
            language={CURRENT_LANGUAGE}
            user-tier={getUserTier()}
          ></lubricator-selection-assistant>
        </div>
      </PurchaseTypeProvider>
    </PurchasePlaceProvider>
  );
};

function getUserTier() {
  if (userIsCustomer()) {
    return "business";
  }
  if (userIsRegistered()) {
    return "plus";
  }
  return "anonymous";
}
